/**
 * PA hook — the NURSE's assigned providers (the provider ids a nurse may view).
 *
 * POST /api/Staff/AssignedProviders  body: { practiceId, startDate, endDate }
 * → returns a flat JSON array of provider-id strings (NO names).
 *
 * Grounded against the Flutter source:
 *   - settings_data_source_impl.dart:1425 fetchStaffAssignedProvidersList →
 *     `client.post('${practiceAPI(PracticeService.Staff)}/AssignedProviders', ...)`
 *     → base + `/api/Staff/AssignedProviders`; response parsed as
 *     `List<String>.from(json.decode(...).map((x) => x))` (provider ids only).
 *   - settings_bloc.dart:1245 StaffAssignedProvidersListEvent →
 *     StaffAssignedProvidersListState(providerIds: List<String>).
 *   - appointment_list_widget.dart:231 nurseAssignedProviders = state.providerIds,
 *     then loadAppointment() sends `practitioners: providerId`.
 *   - request model assigned_providers_request_model.dart: { practiceId, startDate, endDate }.
 *
 * NOTE (names): this endpoint returns ids ONLY. Provider display names are
 * resolved downstream from the fetched appointments' provider participants
 * (see lib/to-doctor-events.ts buildDoctorResources), with a fallback for a
 * provider that has no appointment that day.
 */
import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { practiceNow } from '@/shared/lib/practice-time';

/** Mirrors Flutter AssignedProvidersRequest (assigned_providers_request_model.dart). */
export interface AssignedProvidersRequest {
  practiceId: string;
  /** Full-day DATETIME bounds (the nurse's day window). */
  startDate: string;
  endDate: string;
}

export interface UseAssignedProvidersParams {
  practiceId?: string | null;
  /** YYYY-MM-DD; defaults to today. Widened to a full-day range for the request. */
  date?: string;
}

/** Full-day DATETIME range for `date` (YYYY-MM-DD), or today when omitted. */
function dayRange(date?: string): { startDate: string; endDate: string } {
  let day = date;
  if (!day) {
    const now = practiceNow();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    day = `${yyyy}-${mm}-${dd}`;
  }
  return {
    startDate: `${day}T00:00:00.000`,
    endDate: `${day}T23:59:59.000`,
  };
}

/**
 * Fetch the nurse's assigned provider ids for a practice + day.
 * Disabled (returns no data) until a practiceId is known.
 */
export function useAssignedProviders(params: UseAssignedProvidersParams | null) {
  return useQuery({
    queryKey: ['pa-assigned-providers', params?.practiceId, params?.date ?? 'today'],
    queryFn: ({ signal }) => {
      if (!params?.practiceId) {
        throw new Error('useAssignedProviders called without practiceId');
      }
      const { startDate, endDate } = dayRange(params.date);
      const body: AssignedProvidersRequest = {
        practiceId: params.practiceId,
        startDate,
        endDate,
      };
      return post<string[]>('/api/Staff/AssignedProviders', body, { signal });
    },
    enabled: !!params?.practiceId,
  });
}
